import ClearIcon from "@mui/icons-material/Clear";
import SearchIcon from "@mui/icons-material/Search";
import { Box, Divider, IconButton, Typography } from "@mui/material";
import React, { useEffect, useId, useRef, useState } from "react";
import AcxMainTextField from "../AcxMainTextFieldGrid";

export const MIN_SEARCH_LENGTH = 3;

// Viewers supply presentation and selection callbacks; lookup and scrolling
// remain with the viewer that owns the transcript, chat, or virtualized rows.
export type ConversationSearchResult = {
    key: React.Key;
    speaker?: string;
    speakerColor?: string;
    timestamp?: React.ReactNode;
    content: React.ReactNode;
    onSelect: () => void;
};

type ConversationSearchProps = {
    searchString: string;
    onSearchStringChange: (value: string) => void;
    results: ConversationSearchResult[];
    disabled?: boolean;
    alignment?: "left" | "right";
    inputWidth?: string;
    fitResultsToInput?: boolean;
};

export function ConversationSearch({
    searchString,
    onSearchStringChange,
    results,
    disabled = false,
    alignment = "right",
    inputWidth = "280px",
    fitResultsToInput = false,
}: ConversationSearchProps) {
    const id = useId();
    const wrapperRef = useRef<HTMLDivElement>(null);
    const [showResults, setShowResults] = useState(false);
    const resultsVisible =
        !disabled &&
        showResults &&
        searchString.length >= MIN_SEARCH_LENGTH &&
        results.length > 0;

    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (!wrapperRef.current?.contains(event.target as Node)) {
                setShowResults(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () =>
            document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    useEffect(() => {
        if (!searchString || disabled) setShowResults(false);
    }, [searchString, disabled]);

    return (
        <Box
            ref={wrapperRef}
            sx={{ position: "relative", width: inputWidth, maxWidth: "100%" }}
            onKeyDown={(event) => {
                if (event.key === "Escape" && resultsVisible) {
                    event.stopPropagation();
                    setShowResults(false);
                }
            }}
        >
            <AcxMainTextField
                id={`${id}-input`}
                disabled={disabled}
                value={searchString}
                placeholderText="Search within conversation..."
                inputProps={{
                    "aria-label": "Search within conversation",
                    "aria-controls": resultsVisible
                        ? `${id}-results`
                        : undefined,
                }}
                onChange={(event) => {
                    onSearchStringChange(event.target.value);
                    setShowResults(
                        event.target.value.length >= MIN_SEARCH_LENGTH,
                    );
                }}
                onFocus={() => {
                    if (results.length) setShowResults(true);
                }}
                startAdornment={<SearchIcon fontSize="small" sx={{ ml: 1 }} />}
                endAdornment={
                    searchString !== "" && (
                        <IconButton
                            size="small"
                            disabled={disabled}
                            aria-label="Clear conversation search"
                            onClick={() => {
                                onSearchStringChange("");
                                setShowResults(false);
                            }}
                        >
                            <ClearIcon fontSize="small" />
                        </IconButton>
                    )
                }
            />
            {resultsVisible && (
                <Box
                    id={`${id}-results`}
                    role="region"
                    aria-label="Conversation search results"
                    sx={(theme) => ({
                        display: "flex",
                        padding: "20px 10px",
                        flexDirection: "column",
                        gap: 1,
                        borderRadius: 2,
                        border: `1px solid ${theme.palette.gray[200]}`,
                        boxShadow:
                            "0px 10px 15px 0px rgba(0, 0, 0, 0.10), 0px 4px 6px 0px rgba(0, 0, 0, 0.05)",
                        overflowY: "auto",
                        position: "absolute",
                        top: "110%",
                        [alignment === "right" ? "right" : "left"]: 0,
                        width: fitResultsToInput
                            ? "100%"
                            : { xs: "430px", lg: "568px" },
                        maxWidth: "calc(100vw - 32px)",
                        boxSizing: "border-box",
                        maxHeight: "276px",
                        backgroundColor: theme.palette.background.paper,
                        zIndex: 1000,
                    })}
                >
                    <Typography
                        className="highlightIgnore"
                        sx={{
                            fontWeight: "fontWeightLight",
                            color: "gray.600",
                        }}
                    >
                        {results.length} results for '{searchString}'
                    </Typography>
                    <Box>
                        {results.map((result) => (
                            <React.Fragment key={result.key}>
                                <Box
                                    role="button"
                                    tabIndex={0}
                                    sx={{
                                        p: 1,
                                        width: "100%",
                                        display: "flex",
                                        flexDirection: "column",
                                        gap: 1,
                                        borderRadius: 1,
                                        overflowWrap: "anywhere",
                                        transition:
                                            "background-color 0.2s ease",
                                        "&:hover, &:focus-visible": {
                                            cursor: "pointer",
                                            backgroundColor: "primary.light",
                                        },
                                    }}
                                    onClick={() => {
                                        result.onSelect();
                                        setShowResults(false);
                                    }}
                                    onKeyDown={(event: React.KeyboardEvent) => {
                                        if (
                                            event.key === "Enter" ||
                                            event.key === " "
                                        ) {
                                            event.preventDefault();
                                            result.onSelect();
                                            setShowResults(false);
                                        }
                                    }}
                                >
                                    <Box sx={{ display: "flex", gap: 1 }}>
                                        <Typography
                                            className="highlightIgnore"
                                            sx={{
                                                color:
                                                    result.speakerColor ??
                                                    "gray.500",
                                                fontWeight: "fontWeightBold",
                                            }}
                                        >
                                            {result.speaker}
                                        </Typography>
                                        <Typography
                                            className="highlightIgnore"
                                            sx={{ color: "gray.500" }}
                                        >
                                            {result.timestamp}
                                        </Typography>
                                    </Box>
                                    <Box>{result.content}</Box>
                                </Box>
                                <Divider sx={{ my: 1 }} />
                            </React.Fragment>
                        ))}
                    </Box>
                </Box>
            )}
        </Box>
    );
}
